import useSWR, { preload } from 'swr';
import { getAuthenticatedHeaders } from './fetchHeaders';
import useSWRInfinite from 'swr/infinite';
import { logFailedRequestTiming, logRequestTiming } from './insightsHelper';

export const useFaultContent = (familyNameId, faultIdentifier, moduleTypeId) => {
    const startTime = performance.now(); // Start timing
    const { data, error, isValidating, isLoading, mutate } = useSWR(
        `/api/v1/fault/content/${faultIdentifier}/${familyNameId}${!!moduleTypeId ? `?moduleTypeId=${moduleTypeId}` : ''}`,
        contentFetcher,
        {
            ...swrConfig,
            onSuccess: (_data, key) => {
                const endTime = performance.now(); // End timing
                key = key.replace('$inf$', '');
                logRequestTiming(key, Math.round(endTime - startTime));
            },
            onError: (_error, key) => {
                const endTime = performance.now(); // End timing
                key = key.replace('$inf$', '');
                logFailedRequestTiming(key, Math.round(endTime - startTime));
            }

        });

    return { data, error, isValidating, isLoading, mutate };
}

export const useFaultContentById = (detailId, feature) => {
    const startTime = performance.now(); // Start timing
    const { data, error, isValidating, isLoading, mutate } = useSWR(
        `/api/v1/fault/content/id/${detailId}${!!feature ? `?feature=${feature}` : ''}`,
        contentFetcher,
        {
            ...swrConfig,
            onSuccess: (_data, key) => {
                const endTime = performance.now(); // End timing
                key = key.replace('$inf$', '');
                logRequestTiming(key, Math.round(endTime - startTime));
            },
            onError: (_error, key) => {
                const endTime = performance.now(); // End timing
                key = key.replace('$inf$', '');
                logFailedRequestTiming(key, Math.round(endTime - startTime));
            }

        });

    return { data, error, isValidating, isLoading, mutate };
}

export const useFaultList = (listParams) => {
    const startTime = performance.now(); // Start timing
    const { data, error, isValidating, isLoading, mutate } = useSWR(
        getFaultListPath(listParams),
        jsonFetcher,
        {
            ...swrConfig,
            onSuccess: (_data, key) => {
                const endTime = performance.now(); // End timing
                key = key.replace('$inf$', '');
                logRequestTiming(key, Math.round(endTime - startTime));
            },
            onError: (_error, key) => {
                const endTime = performance.now(); // End timing
                key = key.replace('$inf$', '');
                logFailedRequestTiming(key, Math.round(endTime - startTime));
            }

        });

    const { items, totalRecords } = data || { items: [], totalRecords: 0 };

    return { items, totalRecords, error, isValidating, isLoading, mutate };
}

export const useLegacyFaultContent = (familyNameId, legacyName, moduleTypeId) => {
    const startTime = performance.now(); // Start timing
    const { data, error, isValidating, isLoading, mutate } = useSWR(
        `/api/v1/fault/content/legacy/${legacyName}/${familyNameId}${!!moduleTypeId ? `?moduleTypeId=${moduleTypeId}` : ''}`,
        contentFetcher,
        {
            ...swrConfig,
            onSuccess: (_data, key) => {
                const endTime = performance.now(); // End timing
                key = key.replace('$inf$', '');
                logRequestTiming(key, Math.round(endTime - startTime));
            },
            onError: (_error, key) => {
                const endTime = performance.now(); // End timing
                key = key.replace('$inf$', '');
                logFailedRequestTiming(key, Math.round(endTime - startTime));
            }

        });

    return { data, error, isValidating, isLoading, mutate };
}

const faultListTake = 50;

export const preloadFaultList = (pageIndex, listParams) => {
    preload(getListInfiniteKey(pageIndex || 0, null, { ...listParams, take: faultListTake }), jsonFetcher);
}

export const useFaultListInfinite = (listParams) => {
    const startTime = performance.now(); // Start timing
    const { data, error, isLoading, isValidating, mutate, size, setSize } = useSWRInfinite(
        (pageIndex, previousPageData) => getListInfiniteKey(pageIndex, previousPageData, { ...listParams, take: faultListTake }),
        jsonFetcher,
        {
            ...swrConfig, initialSize: 1, revalidateFirstPage: false,
            onSuccess: (data, key) => {
                const endTime = performance.now(); // End timing
                key = key.replace('$inf$', '');
                if (data[0]?.status >= 400) {
                    console.log('logFailedRequestTiming', data);
                    logFailedRequestTiming(key, Math.round(endTime - startTime));
                } else {
                    logRequestTiming(key, Math.round(endTime - startTime));
                }
            },
            onError: (_error, key) => {
                const endTime = performance.now(); // End timing
                key = key.replace('$inf$', '');
                logFailedRequestTiming(key, Math.round(endTime - startTime));
            }
        });

    let totalRecords = 0;
    let faults = [];
    if (!!data) {
        totalRecords = data[0].totalRecords;
        faults = data.reduce((accumulator, currentValue) => accumulator.concat(currentValue.items), []);
    }

    return {
        faults, totalRecords, error, isLoading, isValidating, mutate, size, setSize
    };
}

const getListInfiniteKey = (pageIndex, previousPageData, listParams) => {
    const { take, query, familyNameId } = listParams;
    const path = `/api/v1/fault/list`;
    const params = new URLSearchParams('')
    params.set('take', take);

    if (!!query) {
        params.set('query', query);
    } else {
        params.set('query', '');
    }

    if (!!familyNameId && familyNameId !== 'undefined') {
        params.set('family', familyNameId);
    }

    if (previousPageData && !previousPageData.items.length) return null // reached the end

    params.set('skip', pageIndex * take)
    return `${path}?${params.toString()}`                    // SWR key
}

const getFaultListPath = (listParams) => {
    const { take, skip, query, familyNameId } = listParams;
    const path = `/api/v1/fault/list`;
    const params = new URLSearchParams('')
    params.set('take', take);
    params.set('skip', skip || 0);

    if (!!query) {
        params.set('query', query);
    } else {
        params.set('query', '');
    }
    if (!!familyNameId) {
        params.set('family', familyNameId);
    }
    else {
        params.delete('family');
    }

    return `${path}?${params.toString()}`;
}

const contentFetcher = (url) => {
    return getAuthenticatedHeaders().then((headers) => {
        return fetch(url, { headers })
            .then(response => response.text());
    });
}

const jsonFetcher = (url) => {
    return getAuthenticatedHeaders().then((headers) => {
        return fetch(url, { headers })
            .then(response => response.json());
    });
}

const swrConfig = {
    revalidateOnFocus: false,
    onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
        // Never retry on 4XX error.
        if (error.status >= 400 && error.status < 500) return;

        //   // Never retry for a specific key.
        //   if (key === '/api/user') return

        // Only retry up to 1 times.
        if (retryCount > 1) return;

        // Retry after 1 seconds.
        setTimeout(() => { revalidate({ retryCount }); }, 1000)
    }
};